import React, { useEffect, useState } from "react";
import { api, fmtMoney, networkLabel } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Sparkles, RefreshCw, Check, X, Undo2, TrendingUp, TrendingDown, Pause } from "lucide-react";
import { toast } from "sonner";

const SEV_ORDER = ["high", "medium", "low"];
const sevStyle = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const statusStyle = {
  implemented: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-zinc-100 text-zinc-600 border-zinc-200",
  reversed: "bg-blue-50 text-blue-700 border-blue-200",
  failed: "bg-red-50 text-red-700 border-red-200",
};

function Tag({ className = "", children }) {
  return (
    <span className={`px-2 py-0.5 text-[11px] font-mono border uppercase tracking-wide ${className}`}>
      {children}
    </span>
  );
}

function actionIcon(kind) {
  if (kind === "increase") return <TrendingUp className="w-3.5 h-3.5" />;
  if (kind === "decrease") return <TrendingDown className="w-3.5 h-3.5" />;
  if (kind === "pause") return <Pause className="w-3.5 h-3.5" />;
  return null;
}

// Human sentence describing exactly what an action does.
function actionSentence(rec) {
  const a = rec.action;
  if (!a) return "";
  const who = `${rec.entity_type} "${rec.entity_name}" (${networkLabel(a.network)})`;
  if (a.kind === "pause") return `Pause ${who}.`;
  const arrow = `${fmtMoney(a.current_value)} → ${fmtMoney(a.proposed_value)}/day`;
  const verb = a.kind === "increase" ? "Increase" : "Decrease";
  return `${verb} daily budget ${arrow} (${a.kind === "increase" ? "+" : "−"}${a.pct}%) on ${who}.`;
}

function Evidence({ ev }) {
  if (!ev || typeof ev !== "object") return null;
  const items = Object.entries(ev).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!items.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono text-zinc-600">
      {items.map(([k, v]) => (
        <span key={k}>
          <span className="text-zinc-400">{k.replace(/_/g, " ")}:</span>{" "}
          {typeof v === "number" ? (/cpa|spend|budget/i.test(k) ? fmtMoney(v) : v) : String(v)}
        </span>
      ))}
    </div>
  );
}

function RecCard({ rec, onImplement, onDecline, busy }) {
  const a = rec.action;
  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid={`rec-${rec.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <Tag className={sevStyle[rec.severity] || sevStyle.low}>{rec.severity}</Tag>
            {rec.category && <Tag className="border-zinc-200 text-zinc-500">{String(rec.category).replace(/_/g, " ")}</Tag>}
            <Tag className="border-zinc-200 text-zinc-500">{rec.entity_type}</Tag>
            {rec.network && <Tag className="border-zinc-200 text-zinc-500">{networkLabel(rec.network)}</Tag>}
            {!rec.actionable && <Tag className="border-zinc-200 text-zinc-400">insight only</Tag>}
          </div>
          <h3 className="font-display font-bold tracking-tight text-lg leading-snug">{rec.title}</h3>
          {rec.entity_name && <div className="text-xs font-mono text-zinc-500 mt-0.5">{rec.entity_name}</div>}
        </div>
        {rec.confidence && (
          <div className="text-right shrink-0">
            <div className="data-label">confidence</div>
            <div className="font-mono text-sm uppercase">{rec.confidence}</div>
          </div>
        )}
      </div>

      {rec.rationale && <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{rec.rationale}</p>}
      <Evidence ev={rec.evidence} />
      {rec.expected_impact && (
        <div className="mt-3 text-xs font-mono text-emerald-700">→ {rec.expected_impact}</div>
      )}

      {a && (
        <div className="mt-4 flex items-center gap-2 text-xs font-mono text-zinc-700 bg-zinc-50 border border-zinc-200 px-3 py-2">
          {actionIcon(a.kind)} <span>{actionSentence(rec)}</span>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        {rec.actionable && (
          <Button
            data-testid={`rec-implement-${rec.id}`}
            disabled={busy}
            onClick={() => onImplement(rec)}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs h-9"
          >
            <Check className="w-4 h-4 mr-1.5" /> Implement
          </Button>
        )}
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => onDecline(rec)}
          className="rounded-none font-mono uppercase tracking-wider text-xs h-9"
        >
          <X className="w-4 h-4 mr-1.5" /> Decline
        </Button>
      </div>
    </div>
  );
}

export default function Recommendations() {
  const [tab, setTab] = useState("active");
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { rec, mode: 'implement' | 'reverse' }

  const load = async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        api.get("/recommendations", { params: { status: "active" } }),
        api.get("/recommendations", { params: { status: "history" } }),
      ]);
      setActive(a.data.recommendations || []);
      setHistory(h.data.recommendations || []);
    } catch (e) {
      toast.error("Failed to load recommendations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/recommendations/generate", null, { params: { days: 7 }, timeout: 180000 });
      toast.success(`Generated ${r.data.count} recommendation(s)`);
      await load();
      setTab("active");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error("Generation failed", { description: msg });
    } finally {
      setGenerating(false);
    }
  };

  const doImplement = async (rec) => {
    setBusyId(rec.id);
    try {
      const r = await api.post(`/recommendations/${rec.id}/implement`);
      if (r.data.ok) toast.success("Implemented", { description: rec.title });
      else toast.error("Failed", { description: r.data.error || "unknown" });
      await load();
    } catch (e) {
      toast.error("Implement failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const doDecline = async (rec) => {
    setBusyId(rec.id);
    try {
      await api.post(`/recommendations/${rec.id}/decline`);
      toast.success("Declined");
      await load();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusyId(null);
    }
  };

  const doReverse = async (rec) => {
    setBusyId(rec.id);
    try {
      const r = await api.post(`/recommendations/${rec.id}/reverse`);
      if (r.data.ok) toast.success("Reversed — prior state restored");
      else toast.error("Reverse failed", { description: r.data.error || "unknown" });
      await load();
    } catch (e) {
      toast.error("Reverse failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const grouped = SEV_ORDER.map((sev) => ({ sev, items: active.filter((r) => r.severity === sev) }))
    .filter((g) => g.items.length);

  return (
    <div>
      <PageHeader
        eyebrow="AI Copilot"
        title="Recommendations"
        description="An objective, best-practice scan of every account, campaign, ad set and ad — ThoughtMetric conversions treated as the source of truth."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={loading || generating}
              onClick={load}
              className="rounded-none font-mono uppercase tracking-wider text-xs h-10"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              data-testid="rec-generate-btn"
              disabled={generating}
              onClick={generate}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs h-10"
            >
              <Sparkles className={`w-4 h-4 mr-1.5 ${generating ? "animate-pulse" : ""}`} />
              {generating ? "Analysing…" : "Generate"}
            </Button>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-none">
          <TabsTrigger value="active" className="rounded-none font-mono uppercase text-xs">
            Active ({active.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-none font-mono uppercase text-xs">
            History ({history.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "active" && (
        <div className="mt-5 space-y-6">
          {generating && (
            <div className="border border-zinc-200 bg-white p-8 text-center">
              <div className="data-label mb-1">Analysing your accounts…</div>
              <div className="font-mono text-sm text-zinc-500">Scanning campaigns, ad sets and ads with an AI lens. This can take up to a minute.</div>
            </div>
          )}
          {!generating && !active.length && (
            <div className="border border-dashed border-zinc-300 bg-white p-10 text-center">
              <Sparkles className="w-6 h-6 mx-auto text-zinc-400 mb-2" />
              <div className="font-display font-bold tracking-tight text-lg">No active recommendations</div>
              <div className="text-sm text-zinc-500 mt-1">Click <span className="font-mono">Generate</span> to run an AI scan of your accounts.</div>
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.sev}>
              <div className="data-label mb-2">{g.sev} priority · {g.items.length}</div>
              <div className="space-y-3">
                {g.items.map((rec) => (
                  <RecCard
                    key={rec.id}
                    rec={rec}
                    busy={busyId === rec.id}
                    onImplement={(r) => setConfirm({ rec: r, mode: "implement" })}
                    onDecline={doDecline}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "history" && (
        <div className="mt-5 border border-zinc-200 bg-white divide-y divide-zinc-100">
          {!history.length && (
            <div className="p-10 text-center text-sm text-zinc-500">No history yet.</div>
          )}
          {history.map((rec) => (
            <div key={rec.id} className="p-4 flex items-start justify-between gap-4" data-testid={`hist-${rec.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Tag className={statusStyle[rec.status] || "border-zinc-200"}>{rec.status}</Tag>
                  {rec.action && <Tag className="border-zinc-200 text-zinc-500">{rec.action.kind}</Tag>}
                  <span className="text-[11px] font-mono text-zinc-400">
                    {rec.implemented_at || rec.declined_at || rec.reversed_at || rec.created_at || ""}
                  </span>
                </div>
                <div className="font-display font-semibold tracking-tight">{rec.title}</div>
                <div className="text-xs font-mono text-zinc-500 mt-0.5">{rec.entity_name}</div>
                {rec.action && <div className="text-xs text-zinc-600 mt-1">{actionSentence(rec)}</div>}
                {rec.error && <div className="text-xs font-mono text-red-600 mt-1">{rec.error}</div>}
              </div>
              {rec.status === "implemented" && (
                <Button
                  variant="outline"
                  disabled={busyId === rec.id}
                  onClick={() => setConfirm({ rec, mode: "reverse" })}
                  data-testid={`rec-reverse-${rec.id}`}
                  className="rounded-none font-mono uppercase tracking-wider text-xs h-9 shrink-0"
                >
                  <Undo2 className="w-4 h-4 mr-1.5" /> Reverse
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.mode === "reverse" ? "Reverse this change?" : "Implement this change?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-sm">
              {confirm?.mode === "reverse"
                ? `This restores the prior budget/status for "${confirm?.rec?.entity_name}". `
                : ""}
              {confirm && actionSentence(confirm.rec)}
              <span className="block mt-2 text-zinc-500">This writes to the live ad account.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-zinc-900 hover:bg-zinc-800"
              onClick={() => (confirm.mode === "reverse" ? doReverse(confirm.rec) : doImplement(confirm.rec))}
            >
              {confirm?.mode === "reverse" ? "Reverse" : "Implement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
